import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaCheck, FaTimes, FaCrown, FaRocket, FaLock, FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { usePlanAccess } from '../../hooks/usePlanAccess';

type Tier = 'BASIC' | 'PRO' | 'ENTERPRISE';

interface PlanInfo {
    tier:        Tier;
    name:        string;
    price:       string;
    priceDesc:   string;
    description: string;
    color:       string;
    icon:        React.ReactNode;
    popular?:    boolean;
    features: Array<{
        included: boolean;
        text:     string;
        note?:    string;
    }>;
}

const PLANS: PlanInfo[] = [
    {
        tier:        'BASIC',
        name:        'Basic',
        price:       'R$ 49,90',
        priceDesc:   '/mês',
        description: 'Ideal para quem está começando e precisa organizar a operação.',
        color:       '#6c757d',
        icon:        <FaLock />,
        features: [
            { included: true,  text: 'KPIs do Dashboard' },
            { included: true,  text: 'Gestão de Clientes' },
            { included: true,  text: 'Gestão de Equipamentos' },
            { included: true,  text: 'Gestão de Planos' },
            { included: true,  text: 'Gestão de Pedidos' },
            { included: true,  text: 'Contas a pagar/receber' },
            { included: true,  text: 'Cobranças manual via WhatsApp' },
            { included: true,  text: '1 Usuário' },
            { included: false, text: 'Integração Boletos Lytex' },
            { included: false, text: 'Emissão de NF automática' },
            { included: false, text: 'Assinatura digital' },
        ],
    },
    {
        tier:        'PRO',
        name:        'Pro',
        price:       'R$ 79,90',
        priceDesc:   '/mês',
        description: 'Para empresas em crescimento que precisam de automação financeira.',
        color:       '#4361ee',
        icon:        <FaRocket />,
        popular:     true,
        features: [
            { included: true,  text: 'Tudo do Basic' },
            { included: true,  text: 'Integração Boletos Lytex' },
            { included: false,  text: 'Emissão de NF automática (indisponivel  no momento)', note: '+R$ 1,00 por nota emitida' },
            // { included: true,  text: '2 Usuários' },
            { included: false, text: 'Envio automático WhatsApp' },
            { included: false, text: 'Envio automático email' },
            { included: false, text: 'TeamZap (multiatendentes)' },
            { included: false, text: 'Assinatura digital' },
            // { included: false, text: 'Níveis de acesso' },
        ],
    },
    {
        tier:        'ENTERPRISE',
        name:        'Enterprise',
        price:       'R$ 149,90',
        priceDesc:   '/mês',
        description: 'Para empresas consolidadas com equipes e automação completa.',
        color:       '#f8961e',
        icon:        <FaCrown />,
        features: [
            { included: true, text: 'Tudo do Pro' },
            { included: true, text: 'Envio automático WhatsApp' },
            { included: true, text: 'Envio automático email' },
            { included: true, text: 'TeamZap (multiatendentes)' },
            { included: false, text: 'Assinatura digital (Autentique - indisponivel no momento)' },
            // { included: true, text: 'Até 5 usuários' },
            // { included: true, text: '3 níveis de acesso', note: 'Operador, Supervisor e Admin' },
            { included: false, text: 'Emissão de NF automática (indisponivel  no momento)', note: '+R$ 1,00 por nota emitida' },
        ],
    },
];

const PLAN_ORDER: Record<Tier, number> = {
    BASIC: 1, PRO: 2, ENTERPRISE: 3,
};

// Número de WhatsApp do comercial (ajuste!)
const WHATSAPP_COMERCIAL = '5588981131487';

export default function PlanosInterno() {
    const { tier: currentTier } = usePlanAccess();

    const handleUpgrade = (targetPlan: PlanInfo) => {
        const mensagem = encodeURIComponent(
            `Olá! Meu plano atual é *${currentTier}* e gostaria de fazer upgrade para o plano *${targetPlan.name}* (${targetPlan.price}${targetPlan.priceDesc}).`
        );
        window.open(`https://wa.me/${WHATSAPP_COMERCIAL}?text=${mensagem}`, '_blank');
    };

    const handleDowngrade = (targetPlan: PlanInfo) => {
        const mensagem = encodeURIComponent(
            `Olá! Meu plano atual é *${currentTier}* e gostaria de fazer downgrade para o plano *${targetPlan.name}*.`
        );
        window.open(`https://wa.me/${WHATSAPP_COMERCIAL}?text=${mensagem}`, '_blank');
    };

    const getButtonConfig = (plan: PlanInfo) => {
        const isCurrent  = plan.tier === currentTier;
        const isUpgrade  = PLAN_ORDER[plan.tier] > PLAN_ORDER[currentTier as Tier];
        const isDowngrade = PLAN_ORDER[plan.tier] < PLAN_ORDER[currentTier as Tier];

        if (isCurrent) {
            return {
                label:    '✓ Seu Plano Atual',
                variant:  'success' as const,
                disabled: true,
                action:   () => {},
            };
        }
        if (isUpgrade) {
            return {
                label:    <><FaRocket className="me-2" />Fazer Upgrade</>,
                variant:  'primary' as const,
                disabled: false,
                action:   () => handleUpgrade(plan),
            };
        }
        return {
            label:    'Solicitar mudança',
            variant:  'outline-secondary' as const,
            disabled: false,
            action:   () => handleDowngrade(plan),
        };
    };

    const currentPlanInfo = PLANS.find(p => p.tier === currentTier);

    return (
        <Container fluid className="p-4">

            {/* ── Cabeçalho ── */}
            <div className="mb-4">
                <h1 className="mb-1">Planos e Assinatura</h1>
                <small className="text-muted">
                    Gerencie seu plano e descubra todos os recursos disponíveis
                </small>
            </div>

            {/* ── Card do plano atual ── */}
            {currentPlanInfo && (
                <Card
                    className="shadow-sm mb-4 border-0"
                    style={{
                        background: `linear-gradient(135deg, ${currentPlanInfo.color}15 0%, ${currentPlanInfo.color}05 100%)`,
                        borderLeft: `4px solid ${currentPlanInfo.color}`,
                    }}
                >
                    <Card.Body>
                        <Row className="align-items-center">
                            <Col md={8}>
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width:           56,
                                            height:          56,
                                            borderRadius:    12,
                                            backgroundColor: `${currentPlanInfo.color}25`,
                                            color:           currentPlanInfo.color,
                                            fontSize:        24,
                                        }}
                                    >
                                        {currentPlanInfo.icon}
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Seu plano atual</small>
                                        <h3 className="mb-0" style={{ color: currentPlanInfo.color }}>
                                            {currentPlanInfo.name}
                                        </h3>
                                        <small className="text-muted">
                                            {currentPlanInfo.price}{currentPlanInfo.priceDesc}
                                        </small>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="text-md-end mt-3 mt-md-0">
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => window.open(`https://wa.me/${WHATSAPP_COMERCIAL}`, '_blank')}
                                >
                                    <FaWhatsapp className="me-2" />
                                    Falar com comercial
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* ── Cards dos planos ── */}
            <Row className="g-4 mb-4">
                {PLANS.map(plan => {
                    const isCurrent = plan.tier === currentTier;
                    const btn       = getButtonConfig(plan);

                    return (
                        <Col key={plan.tier} lg={4} md={6}>
                            <Card
                                className={`h-100 shadow-sm position-relative ${
                                    plan.popular ? 'border-primary border-2' : ''
                                } ${isCurrent ? 'border-success border-2' : ''}`}
                                style={{ transition: 'transform 0.2s' }}
                            >
                                {/* Badge de popular */}
                                {plan.popular && !isCurrent && (
                                    <div
                                        className="position-absolute top-0 start-50 translate-middle"
                                        style={{
                                            backgroundColor: '#4361ee',
                                            color:           '#fff',
                                            padding:         '4px 16px',
                                            borderRadius:    20,
                                            fontSize:        12,
                                            fontWeight:      600,
                                            whiteSpace:      'nowrap',
                                            boxShadow:       '0 2px 8px rgba(67, 97, 238, 0.3)',
                                        }}
                                    >
                                        ⚡ Mais Popular
                                    </div>
                                )}

                                {/* Badge de plano atual */}
                                {isCurrent && (
                                    <div
                                        className="position-absolute top-0 start-50 translate-middle"
                                        style={{
                                            backgroundColor: '#198754',
                                            color:           '#fff',
                                            padding:         '4px 16px',
                                            borderRadius:    20,
                                            fontSize:        12,
                                            fontWeight:      600,
                                            whiteSpace:      'nowrap',
                                            boxShadow:       '0 2px 8px rgba(25, 135, 84, 0.3)',
                                        }}
                                    >
                                        ✓ Plano Atual
                                    </div>
                                )}

                                <Card.Body className="d-flex flex-column p-4">
                                    {/* Ícone + nome */}
                                    <div className="text-center mb-3">
                                        <div
                                            className="d-inline-flex align-items-center justify-content-center mb-2"
                                            style={{
                                                width:           56,
                                                height:          56,
                                                borderRadius:    '50%',
                                                backgroundColor: `${plan.color}20`,
                                                color:           plan.color,
                                                fontSize:        24,
                                            }}
                                        >
                                            {plan.icon}
                                        </div>
                                        <h4 className="mb-0" style={{ color: plan.color }}>
                                            {plan.name}
                                        </h4>
                                    </div>

                                    {/* Preço */}
                                    <div className="text-center mb-3">
                                        <span style={{ fontSize: 32, fontWeight: 700 }}>
                                            {plan.price}
                                        </span>
                                        <span className="text-muted"> {plan.priceDesc}</span>
                                    </div>

                                    {/* Descrição */}
                                    <p className="text-muted text-center small mb-4">
                                        {plan.description}
                                    </p>

                                    {/* Lista de features */}
                                    <ul className="list-unstyled mb-4 flex-grow-1">
                                        {plan.features.map((f, idx) => (
                                            <li
                                                key={idx}
                                                className="d-flex align-items-start mb-2"
                                                style={{ fontSize: 14 }}
                                            >
                                                {f.included ? (
                                                    <FaCheck
                                                        className="me-2 mt-1 flex-shrink-0"
                                                        style={{ color: '#198754' }}
                                                    />
                                                ) : (
                                                    <FaTimes
                                                        className="me-2 mt-1 flex-shrink-0"
                                                        style={{ color: '#dc3545', opacity: 0.6 }}
                                                    />
                                                )}
                                                <span style={{ color: f.included ? 'inherit' : '#999' }}>
                                                    {f.text}
                                                    {f.note && (
                                                        <small className="d-block text-muted" style={{ fontSize: 11 }}>
                                                            {f.note}
                                                        </small>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Botão */}
                                    <Button
                                        variant={btn.variant}
                                        disabled={btn.disabled}
                                        onClick={btn.action}
                                        className="w-100"
                                        size="lg"
                                    >
                                        {btn.label}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* ── Tabela comparativa ── */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Comparativo detalhado</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ minWidth: 260 }}>Funcionalidade</th>
                                    <th className="text-center">Basic</th>
                                    <th className="text-center" style={{ backgroundColor: '#4361ee15' }}>
                                        Pro ⚡
                                    </th>
                                    <th className="text-center">Enterprise</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ['KPIs do Dashboard',              '✓', '✓', '✓'],
                                    ['Gestão de Clientes',             '✓', '✓', '✓'],
                                    ['Gestão de Equipamentos',         '✓', '✓', '✓'],
                                    ['Gestão de Planos',               '✓', '✓', '✓'],
                                    ['Gestão de Pedidos',              '✓', '✓', '✓'],
                                    ['Contas a pagar/receber',         '✓', '✓', '✓'],
                                    ['Cobranças manual WhatsApp',      '✓', '✓', '✓'],
                                    ['Integração Boletos Lytex',       '✗', '✓', '✓'],
                                    ['Emissão de NF automática (indisponivel no momento)',       '✗', '✓ +R$1/nota', '✓ +R$1/nota'],
                                    ['Envio automático WhatsApp',      '✗', '✗', '✓'],
                                    ['Envio automático Email',      '✗', '✗', '✓'],
                                    ['TeamZap (multiatendentes)',      '✗', '✗', '✓'],
                                    ['Assinatura digital (Autentique - indisponivel no momento)','✗', '✗', '✓'],
                                    // ['Usuários',                       '1', '2', 'até 5'],
                                    // ['Níveis de acesso',               '✗', '✗', '✓ 3 níveis'],
                                ].map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{row[0]}</td>
                                        {row.slice(1).map((cell, i) => (
                                            <td
                                                key={i}
                                                className="text-center"
                                                style={{
                                                    backgroundColor: i === 1 ? '#4361ee08' : undefined,
                                                    color: cell === '✗' ? '#dc3545' : cell === '✓' ? '#198754' : 'inherit',
                                                    fontWeight: cell === '✓' || cell === '✗' ? 600 : 400,
                                                }}
                                            >
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>

            {/* ── CTA final ── */}
            <Card className="shadow-sm bg-light border-0">
                <Card.Body className="text-center py-4">
                    <h5 className="mb-2">Ainda tem dúvidas?</h5>
                    <p className="text-muted mb-3">
                        Fale com nosso time comercial e descubra qual plano é ideal para você.
                    </p>
                    <Button
                        variant="success"
                        size="lg"
                        onClick={() => window.open(`https://wa.me/${WHATSAPP_COMERCIAL}`, '_blank')}
                    >
                        <FaWhatsapp className="me-2" />
                        Falar no WhatsApp
                    </Button>
                </Card.Body>
            </Card>

        </Container>
    );
}
